import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useTeam } from '@/contexts/TeamContext';
import { useTags } from '@/hooks/useTags';
import { Tag, TagType, TagDateTrackingMetadata, TagPath, TagTreeNode } from '@/types/tag';

interface TagsContextType {
  tags: Tag[];
  loading: boolean;
  tagTree: TagTreeNode[];
  getChildren: (parentId: string | null) => Tag[];
  getPath: (tagId: string) => TagPath[];
  getById: (id: string) => Tag | undefined;
  getDescendants: (tagId: string) => Tag[];
  getEffectiveDateSettings: (tagId: string) => TagDateTrackingMetadata;
  search: (query: string, type?: TagType) => Tag[];
  addTag: (
    name: string,
    type: TagType,
    parentId?: string | null,
    emoji?: string,
    options?: { color?: string; metadata?: Record<string, unknown> }
  ) => Promise<string | null>;
  updateTag: (
    id: string,
    updates: Partial<Pick<Tag, 'name' | 'emoji' | 'color' | 'metadata' | 'sortOrder' | 'parentId' | 'currentStateId'>>
  ) => Promise<boolean>;
  deleteTag: (id: string) => Promise<boolean>;
  updateTagActiveState: (tagId: string, stateGroupId: string, stateId: string) => Promise<boolean>;
  getTagsByType: (type: TagType) => Tag[];
}

const TagsContext = createContext<TagsContextType | undefined>(undefined);

export function TagsProvider({ children }: { children: ReactNode }) {
  const { currentTeamId } = useTeam();
  const tagsApi = useTags(currentTeamId);

  const getTagsByType = useMemo(() => {
    return (type: TagType) => tagsApi.tags.filter((tag) => tag.tagType === type);
  }, [tagsApi.tags]);

  return (
    <TagsContext.Provider value={{ ...tagsApi, getTagsByType }}>
      {children}
    </TagsContext.Provider>
  );
}

export function useTagsContext() {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error('useTagsContext must be used within a TagsProvider');
  }
  return context;
}
